import { cn } from "../lib/cn";

export interface SwitchProps {
  label: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
}

/**
 * A slim on/off control: a 36 × 20 track with a white thumb. The button keeps
 * the shared touch-target size around it, so the lighter drawing costs no
 * usability. Off is a mid-grey track (a pale one read as disabled); on is the primary action colour, so the
 * state reads from the thumb position and the contrast, never colour alone.
 */
export function Switch({ label, checked, onCheckedChange, disabled = false }: SwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-label={label}
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onCheckedChange(!checked)}
      className="group/switch touch-target inline-flex shrink-0 items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-surface disabled:cursor-not-allowed disabled:opacity-50"
    >
      <span
        aria-hidden
        className={cn(
          "flex h-5 w-9 items-center rounded-full p-0.5 transition-colors motion-reduce:transition-none",
          checked
            ? "bg-action-primary"
            : // Off stays clearly enabled: a mid-grey track, darker on hover — never the pale
              // track a disabled control would have.
              "bg-control-border-hover group-enabled/switch:group-hover/switch:bg-muted",
        )}
      >
        <span
          className={cn(
            "size-4 rounded-full shadow-sm transition-transform motion-reduce:transition-none",
            checked ? "translate-x-4 bg-action-primary-ink" : "translate-x-0 bg-white",
          )}
        />
      </span>
    </button>
  );
}
